import React, { useEffect, useMemo, useState } from 'react';
import { Landmark, CheckCircle2 } from 'lucide-react';
import { toast } from 'react-toastify';
import {
  OPENING_BALANCE_FIELDS,
  useBooksOpening,
  type BooksOpeningBalances,
} from '../lib/booksOpeningStore';
import {
  useFinanceRecords,
  useHandLoanRecords,
} from '../lib/workspaceStore';
import { useCanWrite, hasModuleAccess } from '../lib/permissions';
import { isMigratedLoan } from '../lib/paymentFlows';
import { useTranslation } from '../lib/i18n';
import { erpApi, hasAuthToken, type FinancialYearRecord } from '../api';

const inr = (n: number) => `₹${Math.round(Number(n) || 0).toLocaleString('en-IN')}`;
const num = (v: string) => {
  const n = parseFloat(String(v).replace(/,/g, ''));
  return Number.isFinite(n) ? Math.max(0, n) : 0;
};

type Tab = 'opening' | 'list';

const FY_BALANCE_KEYS = [
  'cash',
  'bank',
  'outstanding',
  'capital',
  'loansTaken',
  'creditors',
  'inventory',
  'employeeAdvances',
] as const;

const hasAnyBalance = (ob: Record<string, number> | undefined) =>
  !!ob && [...FY_BALANCE_KEYS, 'loansGiven'].some((k) => Math.abs(Number(ob[k]) || 0) > 0);

const ymdOf = (iso: string) => String(iso || '').slice(0, 10);

/**
 * The year whose opening balances start the books — the same one Finance ERP uses:
 * the earliest year that already has opening balances, else the year containing the cutover date.
 */
async function loadBooksStartYear(cutoverYmd: string): Promise<FinancialYearRecord | null> {
  const { years } = await erpApi.listFinancialYears();
  const ascending = [...years].sort((a, b) => ymdOf(a.startDate).localeCompare(ymdOf(b.startDate)));
  const base = ascending.find((y) => hasAnyBalance(y.openingBalances));
  if (base) return base;
  const containing = cutoverYmd
    ? ascending.find((y) => ymdOf(y.startDate) <= cutoverYmd && cutoverYmd <= ymdOf(y.endDate))
    : undefined;
  if (containing) return containing;
  return (await erpApi.getCurrentFinancialYear()).year;
}

interface AccountsProps {
  setActiveItem?: (id: string) => void;
}

export function Accounts({ setActiveItem }: AccountsProps) {
  const { t } = useTranslation();
  const canWrite = useCanWrite('accounts');
  const canSyncErp = hasModuleAccess('finance', 'read') || hasModuleAccess('audit', 'read');
  const canOpenErpSuite = hasModuleAccess('audit', 'read');
  const [opening, updateOpening] = useBooksOpening();
  const finances = useFinanceRecords();
  const hands = useHandLoanRecords();
  const [tab, setTab] = useState<Tab>('opening');
  const [fy, setFy] = useState<FinancialYearRecord | null>(null);
  const [savingFy, setSavingFy] = useState(false);

  const migratedFinance = useMemo(
    () => finances.filter((r) => String(r.data?.migrated ?? '').toLowerCase() === 'yes'),
    [finances]
  );
  const migratedHand = useMemo(
    () => hands.filter((r) => String(r.data?.migrated ?? '').toLowerCase() === 'yes'),
    [hands]
  );

  // Only migrated loans belong in the opening balance: loans created in the app already post "Loan given".
  const portfolioOutstanding = useMemo(() => {
    let s = 0;
    for (const r of finances) {
      if (String(r.data?.closed ?? '').toLowerCase() === 'yes') continue;
      if (!isMigratedLoan(r.data as Record<string, unknown>)) continue;
      const out = num(String(r.data?.openingPrincipalOutstanding || r.data?.totalAmount || r.data?.amount || '0'));
      s += out;
    }
    for (const r of hands) {
      if (String(r.data?.closed ?? '').toLowerCase() === 'yes') continue;
      if (!isMigratedLoan(r.data as Record<string, unknown>)) continue;
      const out = num(String(r.data?.openingPrincipalOutstanding || r.data?.loanAmount || '0'));
      s += out;
    }
    return s;
  }, [finances, hands]);

  const applyFyBalances = (year: FinancialYearRecord | null) => {
    setFy(year);
    const ob = year?.openingBalances;
    if (!year || !hasAnyBalance(ob)) return;
    // Finance ERP holds the books' opening balances; show exactly what it has (a 0 stays 0).
    updateOpening((prev) => {
      const balances = { ...prev.balances };
      for (const k of FY_BALANCE_KEYS) {
        balances[k] = Number(k === 'outstanding' ? ob?.loansGiven || ob?.outstanding : ob?.[k]) || 0;
      }
      return { ...prev, balances };
    });
  };

  useEffect(() => {
    if (!hasAuthToken() || !canSyncErp) return;
    loadBooksStartYear(opening.cutoverDate)
      .then(applyFyBalances)
      .catch(() => setFy(null));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [canSyncErp]);

  const fyClosed = fy?.status === 'closed';

  const saveOpening = async () => {
    if (!canWrite) {
      toast.error('You have read-only access.');
      return;
    }
    if (!opening.cutoverDate) {
      toast.error('Set a cutover / go-live date.');
      return;
    }
    updateOpening({ ...opening });
    toast.success('Cutover details saved.');

    if (!hasAuthToken() || !canSyncErp) return;
    setSavingFy(true);
    try {
      const target = await loadBooksStartYear(opening.cutoverDate);
      if (!target) return;
      if (target.status === 'closed') {
        setFy(target);
        toast.info(`${target.label} is closed, so its opening balances were not changed in Finance ERP.`);
        return;
      }
      const erpBalances: Record<string, number> = {};
      for (const k of FY_BALANCE_KEYS) erpBalances[k] = opening.balances[k] || 0;
      const res = await erpApi.updateFinancialYear(target.id, { openingBalances: erpBalances });
      setFy(res.year);
      toast.success(`Opening balances saved to Finance ERP (${res.year.label}).`);
    } catch (e) {
      toast.warn(e instanceof Error ? e.message : 'Saved locally; ERP sync failed — edit in Finance ERP.');
    } finally {
      setSavingFy(false);
    }
  };

  const setBal = (key: keyof BooksOpeningBalances, value: string) => {
    updateOpening((prev) => ({
      ...prev,
      balances: { ...prev.balances, [key]: num(value) },
    }));
  };

  const erpBalanceKeys = OPENING_BALANCE_FIELDS.filter(
    (f) => f.key !== 'ytdInterestIncome' && f.key !== 'ytdExpenses'
  );
  const ytdKeys = OPENING_BALANCE_FIELDS.filter(
    (f) => f.key === 'ytdInterestIncome' || f.key === 'ytdExpenses'
  );

  return (
    <div className="app-page space-y-6">
      <header className="app-page-header">
        <div>
          <p className="text-[10px] font-semibold uppercase tracking-[0.16em] text-emerald-800">{t('nav.accounts')}</p>
          <h1 className="app-page-title mt-0.5">{t('accounts.title')}</h1>
          <p className="app-page-sub">{t('accounts.subtitle')}</p>
        </div>
      </header>

      <div className="grid w-full grid-cols-2 gap-1 rounded-lg border border-zinc-200 bg-white p-1 dark:border-zinc-700 dark:bg-zinc-900 sm:inline-flex sm:w-auto sm:grid-cols-none">
        {(
          [
            { id: 'opening' as const, label: t('accounts.tabOpening') },
            { id: 'list' as const, label: t('accounts.tabMigrated') },
          ] as const
        ).map((item) => (
          <button
            key={item.id}
            type="button"
            onClick={() => setTab(item.id)}
            className={`rounded-md px-2 py-1.5 text-xs font-semibold transition sm:px-3 sm:text-sm ${
              tab === item.id ? 'bg-emerald-600 text-white' : 'text-zinc-600 hover:bg-zinc-50 dark:text-zinc-300 dark:hover:bg-zinc-800'
            }`}
          >
            {item.label}
          </button>
        ))}
      </div>

      {tab === 'opening' && (
        <section className="space-y-4">
          <div className="app-card app-card-inner space-y-4">
            <div className="flex flex-wrap items-start justify-between gap-2">
              <div className="flex items-center gap-2 text-emerald-800">
                <Landmark className="h-4 w-4" />
                <h2 className="text-sm font-semibold text-zinc-900 dark:text-zinc-100">{t('accounts.openingHeading')}</h2>
              </div>
              {canOpenErpSuite ? (
                <button
                  type="button"
                  className="app-btn-secondary text-xs"
                  onClick={() => setActiveItem?.('erp')}
                >
                  {t('accounts.openErpFy')}
                </button>
              ) : null}
            </div>
            <p className="rounded-lg border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-950 dark:border-sky-800/60 dark:bg-sky-950/35 dark:text-sky-100">
              {canSyncErp ? t('accounts.erpSharedHint') : t('accounts.localOpeningHint')}
              {fy ? (
                <span className="mt-1 block font-medium">
                  Books start in {fy.label} ({fy.status}) — these are the opening balances Finance ERP uses.
                </span>
              ) : null}
            </p>
            {fyClosed ? (
              <p className="rounded-lg border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900 dark:border-amber-800/50 dark:bg-amber-950/30 dark:text-amber-100">
                {fy?.label} is closed, so its opening balances and pre-go-live figures are locked. The owner can reopen it in
                Finance ERP → Financial years if a correction is needed.
              </p>
            ) : null}
            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
              <div>
                <label className="app-label">{t('accounts.cutoverDate')}</label>
                <input
                  type="date"
                  className="app-field app-field-date"
                  value={opening.cutoverDate}
                  disabled={!canWrite}
                  onChange={(e) => updateOpening({ cutoverDate: e.target.value })}
                />
                <p className="mt-1 text-xs text-zinc-500">{t('accounts.cutoverHint')}</p>
              </div>
              <div className="rounded-lg border border-zinc-200 bg-zinc-50 px-3 py-2 text-sm dark:border-zinc-700 dark:bg-zinc-800/90">
                <p className="text-xs font-medium text-zinc-500 dark:text-zinc-400">{t('accounts.portfolioCheck')}</p>
                <p className="mt-0.5 font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{inr(portfolioOutstanding)}</p>
                <p className="mt-1 text-[11px] text-zinc-500 dark:text-zinc-400">
                  {t('accounts.booksOutstanding')}: {inr(opening.balances.outstanding)}
                  {Math.abs(portfolioOutstanding - opening.balances.outstanding) > 1 ? (
                    <span className="ml-1 text-amber-700"> · {t('accounts.mismatchHint')}</span>
                  ) : (
                    <span className="ml-1 text-emerald-700"> · {t('accounts.matchedHint')}</span>
                  )}
                </p>
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                {t('accounts.fyBalancesHeading')}
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
                {erpBalanceKeys.map((f) => (
                  <div key={f.key}>
                    <label className="app-label">{f.label}</label>
                    <input
                      type="number"
                      min={0}
                      step="1"
                      className="app-field"
                      disabled={!canWrite || fyClosed}
                      value={opening.balances[f.key] || ''}
                      onChange={(e) => setBal(f.key, e.target.value)}
                      placeholder="0"
                    />
                    <p className="mt-1 text-[11px] text-zinc-500">{f.hint}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <h3 className="mb-2 text-xs font-semibold uppercase tracking-wide text-zinc-500">
                {t('accounts.ytdHeading')}
              </h3>
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                {ytdKeys.map((f) => (
                  <div key={f.key}>
                    <label className="app-label">{f.label}</label>
                    <input
                      type="number"
                      min={0}
                      step="1"
                      className="app-field"
                      disabled={!canWrite || fyClosed}
                      value={opening.balances[f.key] || ''}
                      onChange={(e) => setBal(f.key, e.target.value)}
                      placeholder="0"
                    />
                    <p className="mt-1 text-[11px] text-zinc-500">{f.hint}</p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <label className="app-label">{t('accounts.notes')}</label>
              <textarea
                className="app-field min-h-[72px]"
                disabled={!canWrite}
                value={opening.notes}
                onChange={(e) => updateOpening({ notes: e.target.value })}
                placeholder={t('accounts.notesPlaceholder')}
              />
            </div>

            <div className="flex flex-wrap items-center gap-2">
              <button type="button" className="app-btn-primary" disabled={!canWrite || savingFy} onClick={() => void saveOpening()}>
                <CheckCircle2 className="mr-1.5 h-4 w-4" />
                {savingFy ? t('common.loading') : t('accounts.saveOpening')}
              </button>
              <button
                type="button"
                className="app-btn-secondary"
                disabled={!canWrite || fyClosed}
                onClick={() => setBal('outstanding', String(Math.round(portfolioOutstanding)))}
              >
                {t('accounts.fillFromPortfolio')}
              </button>
            </div>
          </div>
        </section>
      )}

      {tab === 'list' && (
        <section className="space-y-4">
          <div className="app-card app-card-inner">
            <h2 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {t('accounts.migratedFinance')} ({migratedFinance.length})
            </h2>
            {migratedFinance.length === 0 ? (
              <p className="text-sm text-zinc-500">{t('accounts.noneMigrated')}</p>
            ) : (
              <div className="app-table-wrap">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th>{t('common.name')}</th>
                      <th>Reg / HA</th>
                      <th className="text-right">{t('common.outstanding')}</th>
                      <th>EMIs paid before</th>
                      <th>Next due</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {migratedFinance.map((r) => (
                      <tr key={r.id}>
                        <td>{String(r.data.customerName || '—')}</td>
                        <td>{String(r.data.regNo || r.data.haNumber || '—')}</td>
                        <td className="text-right tabular-nums">
                          {inr(num(String(r.data.openingPrincipalOutstanding || r.data.totalAmount || 0)))}
                        </td>
                        <td>{String(r.data.emisPaidBeforeCutover || '0')}</td>
                        <td>{String(r.data.nextDueDate || '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="app-card app-card-inner">
            <h2 className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">
              {t('accounts.migratedHand')} ({migratedHand.length})
            </h2>
            {migratedHand.length === 0 ? (
              <p className="text-sm text-zinc-500">{t('accounts.noneMigrated')}</p>
            ) : (
              <div className="app-table-wrap">
                <table className="app-table">
                  <thead>
                    <tr>
                      <th>{t('common.name')}</th>
                      <th>Type</th>
                      <th className="text-right">{t('common.outstanding')}</th>
                      <th>Interest due</th>
                      <th>Cutover</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-zinc-100">
                    {migratedHand.map((r) => (
                      <tr key={r.id}>
                        <td>{String(r.data.name || '—')}</td>
                        <td>{String(r.data.loanType || r.data.repaymentType || '—')}</td>
                        <td className="text-right tabular-nums">
                          {inr(num(String(r.data.openingPrincipalOutstanding || r.data.loanAmount || 0)))}
                        </td>
                        <td className="tabular-nums">{inr(num(String(r.data.openingInterestDue || 0)))}</td>
                        <td>{String(r.data.migrationCutoverDate || '—')}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </section>
      )}
    </div>
  );
}

export default Accounts;
